import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

export type InvoiceType = "material" | "subcontractor" | "labour";

export interface InvoiceHeader {
  id: string;
  invoiceNumber: string;
  invoiceType: InvoiceType;
  invoiceDate: string;
  status: string;
  partyKind: string;
  partyMasterId: string;
  partyFirmName: string;
  partyGstin: string;
  partyAddress: string;
  billFromText: string;
  billToText: string;
  beneficiaryCount: number;
  selectionMode: "single" | "consolidated";
  sourcePoId: string | null;
  sourceMilestoneTemplateId: string | null;
  sourceMilestoneTitle: string | null;
  subcontractorMasterId: string | null;
  notes: string;
  taxableTotal: number;
  grandTotal: number;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceItem {
  id: string;
  invoiceId: string;
  applicationId: string | null;
  beneficiaryName: string;
  beneficiaryMobileApplicationId: string;
  lineKey: string;
  lineLabel: string;
  lineUnit: string;
  qty: number;
  rate: number;
  lineAmount: number;
  hsnCode: string;
  catalogStandardItemId: string;
  sourcePoId: string | null;
  sourcePoItemId: string | null;
  sourceMilestoneTemplateItemId: string | null;
  createdAt: string;
}

export interface EligibleMaterialLine {
  poItemId: string;
  poId: string;
  poNumber: string;
  applicationId: string;
  beneficiaryName: string;
  beneficiaryMobileApplicationId: string;
  lineLabel: string;
  lineUnit: string;
  poQty: number;
  receivedQty: number;
  alreadyInvoicedQty: number;
  eligibleQty: number;
  rate: number;
  hsnCode: string;
  catalogStandardItemId: string;
  standardResourceItemId: string;
  milestoneTemplateId: string;
  milestoneTitle: string;
  lineKey: string;
}

export interface EligibleSubcontractorLine {
  applicationId: string;
  beneficiaryName: string;
  beneficiaryMobileApplicationId: string;
  milestoneTemplateId: string;
  milestoneTitle: string;
  templateItemId: string;
  lineLabel: string;
  lineUnit: string;
  eligibleQty: number;
  alreadyInvoicedQty: number;
  rate: number;
  hsnCode: string;
  lineKey: string;
}

export interface GenerateInvoiceLineInput {
  applicationId?: string | null;
  beneficiaryName?: string;
  beneficiaryMobileApplicationId?: string;
  lineKey: string;
  lineLabel: string;
  lineUnit?: string;
  qty: number;
  rate: number;
  hsnCode?: string;
  catalogStandardItemId?: string;
  sourcePoId?: string | null;
  sourcePoItemId?: string | null;
  sourceMilestoneTemplateItemId?: string | null;
}

export async function fetchInvoiceRegister(q: {
  limit?: number;
  invoiceType?: InvoiceType;
  search?: string;
} = {}): Promise<InvoiceHeader[]> {
  const params = new URLSearchParams();
  params.set("limit", String(Math.min(2000, Math.max(1, q.limit ?? 500))));
  if (q.invoiceType) params.set("invoiceType", q.invoiceType);
  if (q.search?.trim()) params.set("search", q.search.trim());
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/invoices?${params.toString()}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<InvoiceHeader[]>;
}

export async function fetchInvoiceItems(invoiceId: string): Promise<InvoiceItem[]> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/inventory/invoices/${encodeURIComponent(invoiceId)}/items`,
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<InvoiceItem[]>;
}

export async function fetchEligibleMaterialLines(body: {
  applicationIds?: string[];
  poId?: string;
  milestoneTemplateId?: string;
}): Promise<{ lines: EligibleMaterialLine[] }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/invoices/eligible-material-lines`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ lines: EligibleMaterialLine[] }>;
}

export interface EligibleSubcontractorBeneficiary {
  applicationId: string;
  beneficiaryName: string;
  beneficiaryMobileApplicationId: string;
  subcontractorMasterId: string | null;
  invoiceEligible: boolean;
  skipReason?: string | null;
}

export async function fetchEligibleSubcontractorLines(body: {
  sourceMode?: "milestone" | "timeline";
  applicationIds?: string[];
  milestoneTemplateId?: string;
  timelinePhaseIds?: string[];
}): Promise<{
  sourceMode: "milestone" | "timeline";
  applicationIds: string[];
  completedApplicationIds: string[];
  beneficiaries: EligibleSubcontractorBeneficiary[];
  milestoneTemplateId: string | null;
  timelinePhaseIds: string[];
  lines: EligibleSubcontractorLine[];
}> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/invoices/eligible-subcontractor-lines`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    sourceMode: "milestone" | "timeline";
    applicationIds: string[];
    completedApplicationIds: string[];
    beneficiaries: EligibleSubcontractorBeneficiary[];
    milestoneTemplateId: string | null;
    timelinePhaseIds: string[];
    lines: EligibleSubcontractorLine[];
  }>;
}

export async function fetchEligibleLabourLines(body: {
  applicationIds?: string[];
  sourceMode?: "milestone" | "timeline";
  milestoneTemplateId?: string;
  timelinePhaseIds?: string[];
}): Promise<{
  sourceMode: "milestone" | "timeline";
  applicationIds: string[];
  completedApplicationIds: string[];
  beneficiaries: EligibleSubcontractorBeneficiary[];
  milestoneTemplateId: string | null;
  timelinePhaseIds: string[];
  lines: EligibleSubcontractorLine[];
}> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/invoices/eligible-labour-lines`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    sourceMode: "milestone" | "timeline";
    applicationIds: string[];
    completedApplicationIds: string[];
    beneficiaries: EligibleSubcontractorBeneficiary[];
    milestoneTemplateId: string | null;
    timelinePhaseIds: string[];
    lines: EligibleSubcontractorLine[];
  }>;
}

export async function generateInvoice(body: {
  invoiceType: InvoiceType;
  applicationIds?: string[];
  selectionMode: "single" | "consolidated";
  partyKind: "subcontractor" | "labour_supplier";
  partyMasterId: string;
  billFromText: string;
  billToText: string;
  sourcePoId?: string;
  sourceMilestoneTemplateId?: string;
  sourceMilestoneTitle?: string;
  notes?: string;
  gstRatePercent?: number;
  lines: GenerateInvoiceLineInput[];
}): Promise<{ invoice: InvoiceHeader; items: InvoiceItem[] }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/invoices/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ invoice: InvoiceHeader; items: InvoiceItem[] }>;
}

export async function cancelInvoice(invoiceId: string): Promise<{ ok: true }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/invoices/${encodeURIComponent(invoiceId)}/cancel`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ ok: true }>;
}
